import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money } from "@/lib/format";
import { Replace, Loader2, Cpu, Plus, Minus, Trash2, CheckCircle2, ArrowRightToLine, MapPin } from "lucide-react";

const amt = (it) => (Number(it.quantity) || 0) * ((Number(it.labor_rate) || 0) + (Number(it.material_rate) || 0) + (Number(it.equipment_rate) || 0));

export default function RevisionsTab({ projectId }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [region, setRegion] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(null);

  const { data: revisions = [], isLoading } = useQuery({ queryKey: ["revisions", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/revisions`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["revisions", projectId] });

  const calc = async () => {
    if (!text.trim()) { toast.error("Describe the change first"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/revisions`, { change_text: text, region });
      setText(""); refresh();
      toast.success("Change priced");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/revisions/${id}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const apply = async (id) => {
    setApplying(id);
    try {
      const { data } = await api.post(`/revisions/${id}/apply`);
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      refresh();
      toast.success(`Applied to estimate — new total ${money(data.grand_total)}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setApplying(null); }
  };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Replace className="h-4 w-4 text-primary" /> Revisions — AI Change Calculator</h3>
        <p className="text-sm text-muted-foreground mt-0.5">Describe anything you want to add or remove at any stage — AI prices the difference at US + local market rates, and you can apply it to the estimate in one click.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <textarea data-testid="revision-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
          placeholder="e.g. Add a 3'x4' skylight and upgrade to quartz countertops (18 lf); remove the tile backsplash (30 sf)."
          className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary resize-none" />
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-popover px-2.5 py-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <input data-testid="revision-region-input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (optional)" className="bg-transparent text-sm outline-none w-40" />
          </div>
          <button data-testid="calc-change-btn" onClick={calc} disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} Calculate Change
          </button>
        </div>
      </div>

      {revisions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">No revisions yet.</div>
      ) : (
        <div className="space-y-4" data-testid="revisions-list">
          {revisions.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-5" data-testid={`revision-${r.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{r.change_text}</p>
                  {r.summary && <p className="text-xs text-muted-foreground mt-0.5">{r.summary}</p>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold text-lg ${r.net_delta >= 0 ? "text-destructive" : "text-emerald-500"}`} data-testid={`revision-delta-${r.id}`}>
                    {r.net_delta >= 0 ? "+" : "−"}{money(Math.abs(r.net_delta))}
                  </div>
                  <div className="text-[9px] font-mono uppercase text-muted-foreground">{r.net_delta >= 0 ? "added cost" : "credit"}</div>
                </div>
              </div>
              <div className="space-y-1.5 mb-3">
                {(r.items || []).map((it, i) => {
                  const isRemove = it.action === "remove";
                  const a = amt(it);
                  return (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5">
                      <span className="flex items-center gap-2 min-w-0">
                        {isRemove ? <Minus className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <Plus className="h-3.5 w-3.5 text-destructive shrink-0" />}
                        <span className="truncate">{it.description}</span>
                        <span className="text-xs text-muted-foreground font-mono shrink-0">{Number(it.quantity).toLocaleString()} {it.unit}</span>
                      </span>
                      <span className={`font-mono tabular-nums shrink-0 ${isRemove ? "text-emerald-500" : ""}`}>{isRemove ? "−" : "+"}{money(a)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button data-testid={`revision-delete-${r.id}`} onClick={() => del(r.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" />Delete</button>
                {r.applied ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500"><CheckCircle2 className="h-4 w-4" />Applied{r.co_number ? ` · ${r.co_number}` : ""}{r.client_status === "approved" ? " · client approved" : r.client_status === "rejected" ? " · client rejected" : " · awaiting client"}</span>
                ) : (
                  <button data-testid={`revision-apply-${r.id}`} onClick={() => apply(r.id)} disabled={applying === r.id}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-sm font-bold hover:bg-orange-600 transition-colors disabled:opacity-60">
                    {applying === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightToLine className="h-4 w-4" />} Apply to Estimate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
